import type { RunnerPlatform } from '@/api/runners';
import { cloudProvider, type CloudProvider, PROVIDER_TEXTS } from '@/lib/providers';
import type {
  AnchorKind,
  BurstProviderRequest,
  BurstTemplateRequest,
  JobCapacity,
  ScalingEventType,
  ScalingMetric,
  ScalingPolicy,
  ScalingPolicyRequest,
  ScalingPreset,
  ScalingRule,
} from '@/api/scaling';
import {
  GITHUB_MAX_CONCURRENCY,
  validateGithubConcurrency,
  validateGithubLabels,
  validateGithubOwner,
  validateGithubRepository,
  validateGithubRunnerGroup,
  validateGithubToken,
} from '@/lib/platforms';

export const PRESETS: Record<ScalingPreset, { label: string; rule: ScalingRule }> = {
  EAGER: {
    label: 'Eager',
    rule: { metric: 'FREE_JOB_SLOTS', maxFreeSlots: 1, durationMinutes: 2 },
  },
  BALANCED: {
    label: 'Balanced',
    rule: { metric: 'FREE_JOB_SLOTS', maxFreeSlots: 0, durationMinutes: 3 },
  },
  CONSERVATIVE: {
    label: 'Conservative',
    rule: { metric: 'FREE_JOB_SLOTS', maxFreeSlots: 0, durationMinutes: 5 },
  },
};

export const METRIC_LABELS: Record<ScalingMetric, string> = {
  FREE_JOB_SLOTS: 'Free job slots',
  CPU: 'CPU',
  MEMORY: 'Memory',
};

/** The burst template's executors, labelled like the runner forms' executor select. */
export const EXECUTOR_LABELS: Record<string, string> = {
  DOCKER: 'docker',
  DIND: 'docker-in-docker',
  SHELL: 'shell',
};

export const EVENT_LABELS: Record<ScalingEventType, string> = {
  SCALE_OUT: 'Scaled out',
  SCALE_IN_STARTED: 'Scale-in started',
  BURST_DELETED: 'Burst runner deleted',
  FAILURE: 'Failure',
  POLICY_AUTO_DISABLED: 'Disabled after failures',
  SKIPPED: 'Skipped',
  CLEANUP_BLOCKED: 'Cleanup blocked',
};

export const LIMITS = {
  maxBurstRunners: [1, 20],
  maxFreeSlots: [0, 20],
  stepSize: [1, 5],
  rules: [1, 3],
  durationMinutes: [2, 60],
  scaleInWindowMinutes: [1, 120],
  stabilizationMinutes: [0, 60],
  concurrency: [1, 100],
} as const;

export type LimitField =
  'maxBurstRunners' | 'stepSize' | 'scaleInWindowMinutes' | 'stabilizationMinutes';

/** Labels and explanations of the limits; `name` is the label without its unit. */
export const LIMIT_HELP: Record<
  LimitField,
  { label: string; name: string; short: string; long: string }
> = {
  maxBurstRunners: {
    label: 'Maximum burst runners',
    name: 'Maximum burst runners',
    short:
      'The most burst runners this runner can have at the same time. It caps your Hetzner costs.',
    long: "It counts every burst runner that exists, including ones still starting, draining, or whose cleanup is blocked. Your own runner doesn't count. At most this many extra servers are billed at once.",
  },
  stepSize: {
    label: 'Runners added per step',
    name: 'Runners added per step',
    short: 'How many burst runners are added at once when a rule calls for more capacity.',
    long: 'No more are added while any of them is still starting, and the maximum is never exceeded. Use 1 for a careful start; use more when load usually arrives in big batches, e.g. many pipelines at once. Burst runners are always removed one at a time.',
  },
  scaleInWindowMinutes: {
    label: 'Remove after low load for (minutes)',
    name: 'Remove after low load for',
    short: 'How long the load must stay low before a burst runner is removed.',
    long: '“Low” means that every rule would still be satisfied without that runner, or that no job ran at all. A burst runner is also only removed in the last 15 minutes of its paid Hetzner hour, since that hour is already paid for. A runner can therefore stay up longer than this value. Longer means fewer start/stop cycles; shorter can save money with spiky load.',
  },
  stabilizationMinutes: {
    label: 'Wait after ready (minutes)',
    name: 'Wait after ready',
    short: 'After a new burst runner is ready, wait this long before adding the next one.',
    long: 'It gives the new runner time to pick up queued jobs, so the load figures reflect it before the next decision. Raise it if your jobs take a while to start, e.g. large image pulls. Use 0 to keep scaling as soon as the rules still hold.',
  },
};

/**
 * The limits' explanations for burst runners of [provider]. LIMIT_HELP describes Hetzner, which bills every started
 * hour; providers that bill per second remove a burst runner as soon as the load is low long enough.
 */
export function limitHelp(
  field: LimitField,
  provider: CloudProvider
): (typeof LIMIT_HELP)[LimitField] {
  const help = LIMIT_HELP[field];
  if (provider === 'hetzner') return help;
  const name = PROVIDER_TEXTS[provider].name;
  if (field === 'maxBurstRunners') {
    return {
      ...help,
      short: `The most burst runners this runner can have at the same time. It caps your ${name} costs.`,
    };
  }
  if (field === 'scaleInWindowMinutes') {
    return {
      ...help,
      long: `“Low” means that every rule would still be satisfied without that runner, or that no job ran at all. ${name} bills burst runners per second, so one is removed as soon as the load has stayed low this long. Longer means fewer start/stop cycles; shorter can save money with spiky load.`,
    };
  }
  return help;
}

export type RuleMode = ScalingPreset | 'CUSTOM';

export interface ScalingForm {
  enabled: boolean;
  mode: RuleMode;
  rules: ScalingRule[];
  maxBurstRunners: number;
  stepSize: number;
  scaleInWindowMinutes: number;
  stabilizationMinutes: number;
  /**
   * The burst runners' settings: an external runner's template, or a managed runner's provider choice, with only the
   * provider, server type, location, token and SSH keys; null for a managed runner whose provider is unknown.
   */
  template: BurstTemplateForm | null;
}

/** The anchor's platform decides which settings apply: the GitLab ones and the cache, or `github`. */
export interface BurstTemplateForm {
  platform: RunnerPlatform;
  productId: string;
  location: string;
  gitlabHost: string;
  executor: string;
  concurrency: number;
  sshKeyIds: string[];
  labels: string[];
  cacheId: string;
  metricsConnectionId: string;
  provider: CloudProvider;
  /** The stored template's provider, whose token is stored; null without a stored template. */
  storedProvider: CloudProvider | null;
  /** The API token of `provider`, sent as `hetznerAuthToken` for Hetzner and `providerAuthToken` otherwise. */
  hetznerAuthToken: string;
  runnerToken: string;
  github: GithubTemplateForm;
  /** Whether tokens are stored, so blank fields keep them. */
  tokensStored: boolean;
  /** A managed runner's provider, on which its burst runners copy it; null for an external runner. */
  anchorProvider: CloudProvider | null;
}

export interface GithubTemplateForm {
  scope: 'repository' | 'organization';
  owner: string;
  repository: string;
  runnerGroup: string;
  token: string;
}

/** `platform` is the anchor's, used for a new template; `anchorProvider` a managed anchor's, if known. */
export function scalingForm(
  policy: ScalingPolicy | null,
  kind: AnchorKind,
  platform: RunnerPlatform = 'GITLAB',
  anchorProvider: CloudProvider | null = null
): ScalingForm {
  const template = kind === 'EXTERNAL' ? policy?.template : policy?.burst;
  const external = kind === 'EXTERNAL' ? policy?.template : undefined;
  const github = external?.github;
  return {
    enabled: policy?.enabled ?? true,
    mode: policy ? (policy.preset ?? 'CUSTOM') : 'BALANCED',
    rules: (policy?.rules ?? [PRESETS.BALANCED.rule]).map((r) => ({ ...r })),
    maxBurstRunners: policy?.maxBurstRunners ?? 3,
    stepSize: policy?.stepSize ?? 1,
    scaleInWindowMinutes: policy?.scaleInWindowMinutes ?? 10,
    stabilizationMinutes: policy?.stabilizationMinutes ?? 3,
    template:
      kind === 'EXTERNAL' || anchorProvider
        ? {
            platform: external ? (external.platform ?? 'GITLAB') : platform,
            productId: template?.productId ?? '',
            location: template?.location ?? '',
            gitlabHost: external?.gitlabHost || 'https://gitlab.com',
            executor: external?.executor || 'DOCKER',
            concurrency: external?.concurrency ?? 2,
            sshKeyIds: [...(template?.sshKeyIds ?? [])],
            labels: [...(external?.labels ?? [])],
            cacheId: external?.cacheId ?? '',
            metricsConnectionId: external?.metricsConnectionId ?? '',
            provider: template ? cloudProvider(template.provider) : (anchorProvider ?? 'hetzner'),
            storedProvider: template ? cloudProvider(template.provider) : null,
            hetznerAuthToken: '',
            runnerToken: '',
            github: {
              scope: github && !github.repository ? 'organization' : 'repository',
              owner: github?.owner ?? '',
              repository: github?.repository ?? '',
              runnerGroup: github?.runnerGroup ?? '',
              token: '',
            },
            tokensStored: Boolean(template),
            anchorProvider: kind === 'EXTERNAL' ? null : anchorProvider,
          }
        : null,
  };
}

/** Whether the stored template has a token of the chosen provider, so a blank token field keeps it. */
export function cloudTokenStored(template: BurstTemplateForm): boolean {
  return template.tokensStored && template.provider === template.storedProvider;
}

function templateRequest(template: BurstTemplateForm): BurstTemplateRequest {
  const token = template.hetznerAuthToken.trim() || undefined;
  const common = {
    productId: template.productId,
    location: template.location,
    concurrency: template.concurrency,
    sshKeyIds: template.sshKeyIds,
    labels: template.labels,
    metricsConnectionId: template.metricsConnectionId || undefined,
    // Hetzner templates are sent as before other providers existed, unless they switch back to Hetzner.
    ...(template.provider === 'hetzner'
      ? template.storedProvider && template.storedProvider !== 'hetzner'
        ? { provider: template.provider, hetznerAuthToken: token }
        : { hetznerAuthToken: token }
      : { provider: template.provider, providerAuthToken: token }),
  };
  if (template.platform === 'GITHUB') {
    const { scope, owner, repository, runnerGroup, token } = template.github;
    return {
      ...common,
      platform: 'GITHUB',
      github: {
        owner: owner.trim(),
        ...(scope === 'repository'
          ? { repository: repository.trim() }
          : runnerGroup.trim()
            ? { runnerGroup: runnerGroup.trim() }
            : {}),
        ...(token.trim() ? { token: token.trim() } : {}),
      },
    };
  }
  return {
    ...common,
    gitlabHost: template.gitlabHost.trim(),
    executor: template.executor,
    cacheId: template.cacheId || undefined,
    runnerToken: template.runnerToken.trim() || undefined,
  };
}

/**
 * Burst runners on the anchor's provider are sent as before other providers existed, unless they switch back from
 * another provider.
 */
function burstRequest(template: BurstTemplateForm): BurstProviderRequest | undefined {
  if (template.provider === template.anchorProvider) {
    return template.storedProvider ? { provider: template.provider } : undefined;
  }
  return {
    provider: template.provider,
    productId: template.productId,
    location: template.location,
    providerAuthToken: template.hetznerAuthToken.trim() || undefined,
    sshKeyIds: template.sshKeyIds,
  };
}

export function scalingRequest(form: ScalingForm): ScalingPolicyRequest {
  const template = form.template;
  const managed = !!template?.anchorProvider;
  return {
    enabled: form.enabled,
    maxBurstRunners: form.maxBurstRunners,
    stepSize: form.stepSize,
    ...(form.mode === 'CUSTOM' ? { rules: form.rules.map(ruleFields) } : { preset: form.mode }),
    scaleInWindowMinutes: form.scaleInWindowMinutes,
    stabilizationMinutes: form.stabilizationMinutes,
    template: template && !managed ? templateRequest(template) : undefined,
    burst: template && managed ? burstRequest(template) : undefined,
  };
}

function within(value: number, [min, max]: readonly [number, number]) {
  return Number.isInteger(value) && value >= min && value <= max;
}

/** Field errors keyed like `maxBurstRunners`, `rules.0` or `template.productId`. */
export function validateScalingForm(form: ScalingForm): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!within(form.maxBurstRunners, LIMITS.maxBurstRunners))
    errors.maxBurstRunners = 'Enter 1 to 20.';
  if (!within(form.stepSize, LIMITS.stepSize)) errors.stepSize = 'Enter 1 to 5.';
  if (!within(form.scaleInWindowMinutes, LIMITS.scaleInWindowMinutes)) {
    errors.scaleInWindowMinutes = 'Enter 1 to 120 minutes.';
  }
  if (!within(form.stabilizationMinutes, LIMITS.stabilizationMinutes)) {
    errors.stabilizationMinutes = 'Enter 0 to 60 minutes.';
  }
  if (form.mode === 'CUSTOM') {
    if (form.rules.length < 1 || form.rules.length > 3) errors.rules = 'Add one to three rules.';
    const metrics = form.rules.map((r) => r.metric);
    if (new Set(metrics).size !== metrics.length) errors.rules = 'Use each metric only once.';
    form.rules.forEach((rule, index) => {
      const out = rule.scaleOutThreshold ?? NaN;
      if (rule.metric === 'FREE_JOB_SLOTS') {
        if (!within(rule.maxFreeSlots ?? NaN, LIMITS.maxFreeSlots)) {
          errors[`rules.${index}`] = 'Allow 0 to 20 free job slots.';
        }
      } else if (!within(out, [1, 100])) {
        errors[`rules.${index}`] = 'Scale out at 1 to 100%.';
      } else if (!within(rule.scaleInThreshold ?? NaN, [0, out - 1])) {
        errors[`rules.${index}`] = 'Scale in below a lower percentage than scale out.';
      }
      if (!errors[`rules.${index}`] && !within(rule.durationMinutes, LIMITS.durationMinutes)) {
        errors[`rules.${index}`] = 'Hold for 2 to 60 minutes.';
      }
    });
  }
  const template = form.template;
  if (template && template.provider !== template.anchorProvider) {
    if (!template.productId) errors['template.productId'] = 'Choose a server type.';
    if (!template.location) errors['template.location'] = 'Choose a location.';
    if (!cloudTokenStored(template) && !template.hetznerAuthToken.trim()) {
      errors['template.hetznerAuthToken'] =
        `Enter a ${PROVIDER_TEXTS[template.provider].name} API token.`;
    }
    if (template.anchorProvider) return errors;
    if (template.platform === 'GITHUB') Object.assign(errors, githubTemplateErrors(template));
    else {
      if (!/^https?:\/\/\S+$/.test(template.gitlabHost.trim())) {
        errors['template.gitlabHost'] = 'Enter the URL of your GitLab instance.';
      }
      if (!within(template.concurrency, LIMITS.concurrency))
        errors['template.concurrency'] = 'Enter 1 to 100.';
      if (!template.tokensStored && !template.runnerToken.trim()) {
        errors['template.runnerToken'] = 'Enter the runner authentication token.';
      }
    }
  }
  return errors;
}

function githubTemplateErrors(template: BurstTemplateForm): Record<string, string> {
  const { scope, owner, repository, runnerGroup, token } = template.github;
  const errors: Record<string, string> = {
    'template.github.owner': validateGithubOwner(owner),
    'template.github.repository':
      scope === 'repository' ? validateGithubRepository(repository) : '',
    'template.github.runnerGroup':
      scope === 'organization' ? validateGithubRunnerGroup(runnerGroup) : '',
    'template.github.token':
      !template.tokensStored && !token.trim()
        ? 'Enter a GitHub fine-grained personal access token.'
        : validateGithubToken(token),
    'template.concurrency': validateGithubConcurrency(template.concurrency),
    'template.labels': validateGithubLabels(template.labels),
  };
  return Object.fromEntries(Object.entries(errors).filter(([, message]) => message));
}

export function maxBurstConcurrency(platform: RunnerPlatform): number {
  return platform === 'GITHUB' ? GITHUB_MAX_CONCURRENCY : LIMITS.concurrency[1];
}

export function defaultRule(metric: ScalingMetric): ScalingRule {
  return metric === 'FREE_JOB_SLOTS'
    ? { metric, maxFreeSlots: 0, durationMinutes: 3 }
    : { metric, scaleOutThreshold: 80, durationMinutes: 5, scaleInThreshold: 30 };
}

/** Only the fields of the rule's metric, which the backend requires. */
export function ruleFields(rule: ScalingRule): ScalingRule {
  return rule.metric === 'FREE_JOB_SLOTS'
    ? {
        metric: rule.metric,
        maxFreeSlots: rule.maxFreeSlots,
        durationMinutes: rule.durationMinutes,
      }
    : {
        metric: rule.metric,
        scaleOutThreshold: rule.scaleOutThreshold,
        durationMinutes: rule.durationMinutes,
        scaleInThreshold: rule.scaleInThreshold,
      };
}

/**
 * A rule as its scale-out and scale-in parts. No-break spaces keep only short units together, such as "for 3 min", so a
 * part still wraps in a narrow column.
 */
export function ruleParts(rule: ScalingRule): [string, string] {
  const unit = (text: string) => text.replaceAll(' ', '\u00a0');
  const duration = unit(`for ${rule.durationMinutes} min`);
  if (rule.metric === 'FREE_JOB_SLOTS') {
    const free = rule.maxFreeSlots;
    return [
      `${METRIC_LABELS[rule.metric]} ${unit(`≤ ${free}`)} ${duration},`,
      `remove when ${unit(`more than ${free}`)} would stay free or the group is idle`,
    ];
  }
  return [
    `${METRIC_LABELS[rule.metric]} ${unit(`≥ ${rule.scaleOutThreshold}%`)} ${duration},`,
    `remove when ${unit(`below ${rule.scaleInThreshold}%`)}`,
  ];
}

export function ruleSummary(rule: ScalingRule): string {
  return ruleParts(rule).join(' ').replaceAll('\u00a0', ' ');
}

function plural(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/** For example "3 of 8 job slots busy · 5 free". */
export function capacityText(capacity: JobCapacity): string {
  return `${capacity.busy} of ${plural(capacity.slots, 'job slot')} busy · ${capacity.free} free`;
}

/**
 * The presets worth offering for an anchor with `slots` job slots, null if unknown. One that allows as many free slots
 * as the anchor has would add a runner whenever a job runs, and of presets with the same rule only the first is
 * offered. `current`, the stored preset, is always offered, so a policy keeps showing what it uses.
 */
export function visiblePresets(
  slots: number | null,
  current: ScalingPreset | null = null,
  presets: Record<ScalingPreset, { rule: ScalingRule }> = PRESETS
): ScalingPreset[] {
  const keys = Object.keys(presets) as ScalingPreset[];
  if (slots == null) return keys;
  const rules = new Set<string>();
  return keys.filter((preset) => {
    const { maxFreeSlots = 0, durationMinutes } = presets[preset].rule;
    const key = `${maxFreeSlots}:${durationMinutes}`;
    const offered = maxFreeSlots < slots && !rules.has(key);
    if (offered) rules.add(key);
    return offered || preset === current;
  });
}

/**
 * What a free job slots rule does with `slots` job slots now (null if unknown), for example "With 4 job slots now: a
 * burst runner starts when all 4 are busy for 3 minutes. …". `anchorSlots` splits the count when burst runners add to it.
 */
export function freeSlotsExplanation(
  rule: ScalingRule,
  slots: number | null,
  scaleInWindowMinutes: number,
  anchorSlots: number | null = null
): string {
  const free = rule.maxFreeSlots ?? 0;
  const minutes = plural(rule.durationMinutes, 'minute');
  const window = plural(scaleInWindowMinutes, 'minute');
  const removal =
    free === 0
      ? 'at least 1 job slot would stay free'
      : `more than ${plural(free, 'job slot')} would stay free`;
  const scaleIn = `One is removed again when, without it, ${removal} for ${window}, or when the group ran no job for ${window}.`;
  if (slots == null) {
    const when =
      free === 0
        ? 'no job slot is free'
        : `at most ${plural(free, 'job slot')} ${free === 1 ? 'is' : 'are'} free`;
    return `A burst runner starts when ${when} for ${minutes}. ${scaleIn}`;
  }
  const now =
    anchorSlots != null && slots > anchorSlots
      ? `With ${plural(slots, 'job slot')} now (${anchorSlots} on this runner, ${slots - anchorSlots} on burst runners)`
      : `With ${plural(slots, 'job slot')} now`;
  // With no more slots than may stay free, the rule holds whenever the group runs a job; only idleness removes runners.
  if (free >= slots) {
    return (
      `${now}, this rule always allows another runner, so one is added as soon as a job is seen. Burst runners are ` +
      `removed again once the group has run no job for ${window}.`
    );
  }
  let when: string;
  if (free === 0) when = slots === 1 ? 'when its job slot is busy' : `when all ${slots} are busy`;
  else if (slots - free === 1) when = 'when at least 1 of them is busy';
  else when = `when at least ${slots - free} of them are busy`;
  return `${now}: a burst runner starts ${when} for ${minutes}. ${scaleIn}`;
}

export function scalingSummary(policy: ScalingPolicy): string {
  const rules = policy.preset
    ? PRESETS[policy.preset].label
    : `${policy.rules.length} custom rule${policy.rules.length === 1 ? '' : 's'}`;
  return `Autoscaling · ${rules} · up to ${policy.maxBurstRunners} burst runner${policy.maxBurstRunners === 1 ? '' : 's'}`;
}

export function cleanupBlocked(policy: ScalingPolicy): string | null {
  return policy.status.runners.find((r) => r.cleanupBlocked)?.cleanupBlocked ?? null;
}

/** For example "2 burst runners, 1 starting". */
export function burstStatus(policy: ScalingPolicy): string {
  const { burstRunners, booting, draining } = policy.status;
  const parts = [`${burstRunners} burst runner${burstRunners === 1 ? '' : 's'}`];
  if (booting) parts.push(`${booting} starting`);
  if (draining) parts.push(`${draining} draining`);
  return parts.join(', ');
}

export function scalingHint(policy: ScalingPolicy): string {
  if (policy.status.disabledReason) return policy.status.disabledReason;
  const blocked = cleanupBlocked(policy);
  if (blocked) return `Cleanup blocked: ${blocked}`;
  const runners = burstStatus(policy);
  if (!policy.enabled) return `Disabled · ${runners}`;
  const { capacity, lastReason } = policy.status;
  return [capacity && capacityText(capacity), runners, lastReason].filter(Boolean).join(' · ');
}

export interface BurstTimes {
  /** When the burst runner was reserved. */
  created: string;
  /** When Hetzner created its server, from which it is billed; null without a server. */
  startedAt: string | null;
  starting: boolean;
  /** Being removed, or unknown, like the LEAVING phase of the policy status. */
  draining?: boolean;
}

const SETUP_STATES = new Set(['WAITING_FOR_PAYMENT', 'CREATING', 'CONFIGURING']);

/**
 * The times of a burst runner from its runner state, used by both its card and its policy's status so they agree. A
 * runner still being set up counts from its reservation, even while it is removed; any other one from its server.
 */
export function burstTimes(
  state: string | null,
  leaving: boolean,
  created: string | undefined,
  startedAt: string | null | undefined
): BurstTimes {
  return {
    created: created ?? '',
    startedAt: startedAt ?? null,
    starting: SETUP_STATES.has((state ?? '').toUpperCase()),
    draining: leaving,
  };
}

/** When a burst runner's shown time starts, in ms; for sorting oldest first. */
export function burstSince(times: BurstTimes): number {
  const at = Date.parse((times.starting ? null : times.startedAt) ?? times.created);
  return Number.isNaN(at) ? Infinity : at;
}

function minutesText(ms: number): string {
  const minutes = Math.floor(Math.max(0, ms) / 60_000);
  if (minutes < 1) return 'less than 1 min';
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} min`;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

export function clockTime(at: Date, now: Date): string {
  const time = at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (at.toDateString() === now.toDateString()) return time;
  return `${at.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${time}`;
}

/**
 * How long a burst runner has been starting or running, for example "Running for 1 h 12 min · since 14:02", with the
 * full timestamp as `title`; null when neither is known. A running one counts from its server's creation, as Hetzner
 * bills it.
 */
export function burstRunningTime(
  times: BurstTimes,
  now: number
): { text: string; title: string } | null {
  const shown = elapsedTime(times, now);
  if (!shown || !times.draining) return shown;
  return { ...shown, text: `Draining · ${shown.text}` };
}

function elapsedTime(times: BurstTimes, now: number): { text: string; title: string } | null {
  if (times.starting) {
    const created = new Date(times.created);
    if (Number.isNaN(created.getTime())) return null;
    return {
      text: `Starting for ${minutesText(now - created.getTime())}`,
      title: `Requested ${created.toLocaleString()}`,
    };
  }
  if (!times.startedAt) return null;
  const started = new Date(times.startedAt);
  if (Number.isNaN(started.getTime())) return null;
  const elapsed = now - started.getTime();
  const text =
    elapsed < 3_600_000
      ? `Running for ${minutesText(elapsed)}`
      : `Running for ${minutesText(elapsed)} · since ${clockTime(started, new Date(now))}`;
  return { text, title: `Server created ${started.toLocaleString()}` };
}

/** Free job slots rules need a job limit; an external runner without one cannot be evaluated. */
export function jobLimitMissing(policy: ScalingPolicy): boolean {
  return policy.anchor.jobLimit == null && policy.rules.some((r) => r.metric === 'FREE_JOB_SLOTS');
}

export interface BurstReference {
  anchorRunnerId: string;
}

/**
 * Orders cards so each anchor is followed by its burst runners. Burst runners of an anchor that is not shown, such as
 * an external runner or a filtered-out one, come after the managed runners; `externalAnchors` take theirs along.
 */
export function groupByAnchor<
  R extends { id: string; burst: BurstReference | null },
  E extends { runnerId: string },
>(
  runners: R[],
  externals: E[]
): ({ kind: 'managed'; runner: R } | { kind: 'external'; runner: E })[] {
  const bursts = new Map<string, R[]>();
  for (const runner of runners) {
    if (!runner.burst) continue;
    const list = bursts.get(runner.burst.anchorRunnerId) ?? [];
    list.push(runner);
    bursts.set(runner.burst.anchorRunnerId, list);
  }
  const shown = new Set([
    ...runners.filter((r) => !r.burst).map((r) => r.id),
    ...externals.map((e) => e.runnerId),
  ]);
  const items: ({ kind: 'managed'; runner: R } | { kind: 'external'; runner: E })[] = [];
  for (const runner of runners) {
    if (runner.burst) continue;
    items.push({ kind: 'managed', runner });
    for (const burst of bursts.get(runner.id) ?? []) items.push({ kind: 'managed', runner: burst });
  }
  for (const runner of runners) {
    if (runner.burst && !shown.has(runner.burst.anchorRunnerId))
      items.push({ kind: 'managed', runner });
  }
  for (const external of externals) {
    items.push({ kind: 'external', runner: external });
    for (const burst of bursts.get(external.runnerId) ?? [])
      items.push({ kind: 'managed', runner: burst });
  }
  return items;
}
